import React, { useEffect, useState } from "react";
import { useAuth } from "../../context/AuthContext";

export default function PartnerLibraryPage() {
    const { api } = useAuth();
    const [items, setItems] = useState([]);
    const [workspaces, setWorkspaces] = useState([]);
    const [error, setError] = useState(null);

    const refresh = () => api.get("/v2/partner/assessment-library").then(r => setItems(r.data.items || [])).catch(() => {});

    useEffect(() => {
        refresh();
        api.get("/v2/partner/workspaces").then(r => setWorkspaces(r.data.workspaces || [])).catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div data-testid="partner-library-page">
            <div className="mb-8">
                <h1 className="text-3xl font-display font-bold text-white">Assessment Library</h1>
                <p className="text-sm text-gray-500 mt-1">Standardized assessments you can pull into any workspace in your portfolio.</p>
            </div>

            <AttachForm api={api} workspaces={workspaces} onAttached={() => { refresh(); setError(null); }} setError={setError} />
            {error && <p className="text-xs text-red-300 mt-3" data-testid="library-error">{error}</p>}

            <div className="bg-card border border-border/40 mt-8" data-testid="library-list">
                <div className="grid grid-cols-[2fr_1fr_1fr_1fr_2fr] px-5 py-3 border-b border-border/40 text-xs text-gray-600 uppercase tracking-wider">
                    <span>Label</span><span>Source</span><span>Confidence</span><span>Added</span><span></span>
                </div>
                {items.map(it => (
                    <div key={it.id} className="grid grid-cols-[2fr_1fr_1fr_1fr_2fr] px-5 py-3 border-b border-border/20 items-center"
                        data-testid={`library-row-${it.id}`}>
                        <span className="text-sm text-white">{it.label}</span>
                        <span className="text-xs text-gray-500 font-mono">{it.source}</span>
                        <span className="text-xs text-gray-500 font-mono">{it.parse_confidence != null ? `${Math.round(it.parse_confidence)}%` : "—"}</span>
                        <span className="text-xs text-gray-600 font-mono">{it.added_at?.slice(0, 10)}</span>
                        <PullForm api={api} item={it} workspaces={workspaces} />
                    </div>
                ))}
                {items.length === 0 && (
                    <div className="px-5 py-10 text-xs text-gray-500 text-center">
                        No assessments in your library yet. Approve a manual upload in any workspace, then attach it here.
                    </div>
                )}
            </div>
        </div>
    );
}

function AttachForm({ api, workspaces, onAttached, setError }) {
    const [ws, setWs] = useState("");
    const [tplId, setTplId] = useState("");
    const [label, setLabel] = useState("");
    const [busy, setBusy] = useState(false);

    const attach = async () => {
        if (!tplId) { setError("Paste a template UUID."); return; }
        setBusy(true);
        try {
            await api.post("/v2/partner/assessment-library/attach", { template_id: tplId, label });
            setTplId(""); setLabel(""); onAttached();
        } catch (e) {
            setError(e.response?.data?.detail || "Attach failed");
        } finally { setBusy(false); }
    };

    return (
        <div className="bg-card border border-border/40 p-5" data-testid="attach-form">
            <p className="text-sm font-display text-white mb-1">Attach an existing approved template to your library</p>
            <p className="text-xs text-gray-500 mb-3 font-body">
                Paste an assessment_template UUID from a workspace in your portfolio (visible after manual upload approval). The template is referenced — not duplicated — until pulled into another workspace.
            </p>
            <div className="grid grid-cols-[1fr_2fr_1fr_140px] gap-3">
                <select className="bg-muted border border-border/40 px-3 py-2 text-sm text-white" value={ws} onChange={e => setWs(e.target.value)}>
                    <option value="">Source workspace…</option>
                    {workspaces.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                </select>
                <input className="bg-muted border border-border/40 px-3 py-2 text-sm text-white font-mono" placeholder="template UUID"
                    value={tplId} onChange={e => setTplId(e.target.value)} data-testid="attach-template-id" />
                <input className="bg-muted border border-border/40 px-3 py-2 text-sm text-white" placeholder="label (optional)"
                    value={label} onChange={e => setLabel(e.target.value)} />
                <button onClick={attach} disabled={busy}
                    className="bg-white text-black px-3 py-2 text-sm font-semibold disabled:opacity-40"
                    data-testid="attach-btn">
                    {busy ? "Attaching…" : "Attach"}
                </button>
            </div>
        </div>
    );
}

function PullForm({ api, item, workspaces }) {
    const [target, setTarget] = useState("");
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(null);

    const pull = async () => {
        if (!target) return;
        setBusy(true);
        try {
            const r = await api.post(`/v2/partner/workspaces/${target}/pull-assessment`, { library_item_id: item.id });
            setDone(r.data.template_id);
        } catch (e) {
            alert(e.response?.data?.detail || "Pull failed");
        } finally { setBusy(false); }
    };

    if (done) return <span className="text-xs text-emerald-300 font-mono">Pulled · {done.slice(0, 8)}</span>;

    return (
        <div className="flex gap-2">
            <select className="bg-muted border border-border/40 px-2 py-1 text-xs text-white flex-1"
                value={target} onChange={e => setTarget(e.target.value)} data-testid={`pull-target-${item.id}`}>
                <option value="">Pull into…</option>
                {workspaces.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
            </select>
            <button onClick={pull} disabled={!target || busy}
                className="text-xs px-2 py-1 border border-border/60 text-gray-300 hover:text-white disabled:opacity-40"
                data-testid={`pull-btn-${item.id}`}>
                {busy ? "…" : "Pull"}
            </button>
        </div>
    );
}
